import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { SlowMovingResult, StockAnalysisItem } from './useInventoryApi';

// ─────────────────────────────────────────────────────────────────────────────
// Read-only inventory analysis API — the Accounting V2 valuation and alert
// surfaces that had no screen: stock card, product valuation, low-stock,
// ageing bands, and slow-moving narrowed by location.
//
// Verified against src/api-accounting/inventory.js before wiring, and against
// the services behind it (src/accounting/inventory/valuation-service.js,
// reorder-service.js):
//   GET /api/accounting/inventory/stock-card        productId REQUIRED → else 400
//   GET /api/accounting/inventory/valuation         productId REQUIRED → else 400
//   GET /api/accounting/inventory/alerts/low-stock  ?locationId=
//   GET /api/accounting/inventory/alerts/aging      ?locationId=
//   GET /api/accounting/inventory/alerts/slow-moving ?daysPeriod=&minMovementQty=&locationId=
// All five carry requirePermission(ACCOUNTING_PERMISSIONS.JOURNAL_VIEW), so a
// 403 is a real answer these screens must render rather than hide behind an
// empty table. `useSlowMoving` in useInventoryApi.ts is left untouched: the
// Inventory page's Ageing tab keeps using it, and this module's variant adds
// only the location filter that tab cannot offer.
//
// Amounts on this surface are MINOR units (paise) except on the stock card,
// whose unitCost / movementValue / runningValue are rupee decimals straight from
// the movement document. The two formatters in ../utils/reportFormat keep that
// distinction visible at the call site.
// ─────────────────────────────────────────────────────────────────────────────

const ACCOUNTING_INVENTORY = '/api/accounting/inventory';

/** The { ok, data } envelope every /api/accounting/* endpoint answers with. */
interface AccountingEnvelope<T> {
  ok: boolean;
  data: T;
}

async function getAccounting<T>(path: string, params?: Record<string, string>): Promise<T> {
  const { data } = await api.get<AccountingEnvelope<T>>(`${ACCOUNTING_INVENTORY}${path}`, { params });
  return data.data;
}

/** Query for the two product-scoped endpoints. `productId` is not optional. */
export interface ProductScope {
  productId: string;
  variantId?: string;
  locationId?: string;
}

export interface StockCardQuery extends ProductScope {
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

/** One movement line with the running balance the server already computed. */
export interface StockCardLine {
  _id: string;
  /** The movement's createdAt — the card is ordered by this field. */
  date: string;
  movementType?: string;
  referenceType?: string;
  referenceNumber?: string;
  narration?: string;
  quantityIn: number;
  quantityOut: number;
  /** Rupees per unit on this movement, not paise. */
  unitCost: number;
  movementValue: number;
  runningQty: number;
  runningValue: number;
}

export interface StockCardResult {
  stockCard: StockCardLine[];
  total: number;
  page: number;
  limit: number;
}

/** A remaining FIFO cost layer. Absent for the weighted-average and standard branches. */
export interface ValuationCostLayer {
  receivedAt?: string | null;
  sourceType?: string;
  lotNumber?: string;
  originalQuantity?: number;
  remainingQuantity?: number;
  /** Rupees per unit on the layer, not paise. */
  unitCost?: number;
  layerValue?: number;
}

/**
 * The shape depends on the product's valuationMethod (fifo | weighted_average |
 * standard) — each branch of valuation-service.js emits a different set of
 * fields, so everything but the method, quantity and value is optional and the
 * screen renders only what actually arrived.
 */
export interface ProductValuation {
  method: string;
  totalQty: number;
  totalValueMinor: number;
  weightedAvgCostMinor?: number;
  unitCostMinor?: number;
  varianceFromActualMinor?: number;
  layers?: ValuationCostLayer[];
}

/** `/alerts/low-stock` — safety stock / alert-quantity threshold breach. */
export interface AccountingLowStockAlert {
  productId: string;
  name: string;
  sku?: string;
  currentStock: number;
  threshold: number;
  /** out_of_stock | critical | low. */
  severity: string;
}

/** `/alerts/aging` — stock grouped by the age of its oldest cost layer. */
export interface StockAgingItem {
  productId: string;
  name: string;
  sku?: string;
  locationId?: string | null;
  quantityOnHand: number;
  stockValueMinor: number;
  ageDays: number;
  ageBand: string;
}

export interface StockAgingBandSummary {
  qty: number;
  valueMinor: number;
  itemCount: number;
}

export interface StockAgingResult {
  items: StockAgingItem[];
  bandSummary: Record<string, StockAgingBandSummary>;
  totalItems: number;
}

export type { SlowMovingResult, StockAnalysisItem };

function scopeParams(scope: ProductScope | StockCardQuery): Record<string, string> {
  const params: Record<string, string> = { productId: scope.productId };
  if (scope.variantId) params.variantId = scope.variantId;
  if (scope.locationId) params.locationId = scope.locationId;
  const range = scope as StockCardQuery;
  if (range.startDate) params.startDate = range.startDate;
  if (range.endDate) params.endDate = range.endDate;
  if (range.page) params.page = String(range.page);
  if (range.limit) params.limit = String(range.limit);
  return params;
}

/**
 * Per-product stock card with running quantity and value. Disabled until a
 * product is chosen — the endpoint answers 400 without one, so there is nothing
 * honest to fetch beforehand and no default product to fake.
 */
export function useStockCard(query: StockCardQuery | null) {
  return useQuery({
    queryKey: ['inventory', 'stock-card', query],
    queryFn: () => getAccounting<StockCardResult>('/stock-card', scopeParams(query as StockCardQuery)),
    enabled: !!query?.productId,
  });
}

/** The product's configured costing method, resolved server-side. */
export function useProductValuation(scope: ProductScope | null) {
  return useQuery({
    queryKey: ['inventory', 'valuation', scope],
    queryFn: () => getAccounting<ProductValuation>('/valuation', scopeParams(scope as ProductScope)),
    enabled: !!scope?.productId,
  });
}

/**
 * Accounting low-stock alerts — deliberately separate from `useAlerts()` in
 * useInventoryApi.ts (/api/inventory/alerts), which thresholds on the product's
 * alertQuantity alone. This one ranks safety-stock breaches by severity.
 */
export function useAccountingLowStockAlerts(locationId?: string) {
  return useQuery({
    queryKey: ['inventory', 'alerts', 'low-stock', locationId],
    queryFn: () => getAccounting<{ alerts: AccountingLowStockAlert[]; totalAlerts: number }>(
      '/alerts/low-stock', locationId ? { locationId } : undefined,
    ),
  });
}

/** Stock ageing by band (30 / 60 / 90 / 180 / 365 days) from the oldest cost layer. */
export function useStockAging(locationId?: string) {
  return useQuery({
    queryKey: ['inventory', 'alerts', 'aging', locationId],
    queryFn: () => getAccounting<StockAgingResult>('/alerts/aging', locationId ? { locationId } : undefined),
  });
}

/** Slow / non-moving analysis for a window AND an optional location. */
export function useSlowMovingAnalysis(daysPeriod = 90, locationId?: string) {
  return useQuery({
    queryKey: ['inventory', 'slow-moving', daysPeriod, locationId],
    queryFn: () => getAccounting<SlowMovingResult>('/alerts/slow-moving', {
      daysPeriod: String(daysPeriod),
      ...(locationId ? { locationId } : {}),
    }),
  });
}
